import {
	canonicalDataSourceMetaKey,
	DATA_SOURCE_ICON_META,
} from '@jepto/shared/utils/data-source-icon-meta.util';

/** Title-case a single token (`campaignName` → `Campaign Name`). */
function humanizeFieldSegment(segment: string): string {
	const normalized = segment
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
		.replace(/[_-]+/g, ' ')
		.trim();
	if (!normalized) {
		return segment;
	}
	if (normalized.toLowerCase() === 'date') {
		return 'Date';
	}
	return normalized
		.split(/\s+/)
		.filter(Boolean)
		.map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
		.join(' ');
}

/**
 * Human-readable column / field title from a technical id (`googleAds`, `Unified.Date`,
 * `googleAds.campaignName`). Uses connector labels when the whole key or prefix matches
 * a known data source.
 */
export function humanizeTableColumnHeader(technicalId: string): string {
	const trimmed = technicalId?.trim() ?? '';
	if (!trimmed) {
		return '';
	}

	const connectorKey = canonicalDataSourceMetaKey(trimmed);
	if (connectorKey) {
		return DATA_SOURCE_ICON_META[connectorKey].label;
	}

	const lastDot = trimmed.lastIndexOf('.');
	if (lastDot >= 0) {
		const prefix = trimmed.slice(0, lastDot);
		const suffix = trimmed.slice(lastDot + 1);
		const prefixKey = prefix
			? canonicalDataSourceMetaKey(prefix)
			: undefined;
		if (prefixKey && suffix) {
			return humanizeFieldSegment(suffix);
		}
		const left = humanizeFieldSegment(prefix);
		const right = humanizeFieldSegment(suffix);
		return right ? `${left} ${right}` : left;
	}

	return humanizeFieldSegment(trimmed);
}

/** True when the string already looks like a display label (not a raw schema key). */
export function isHumanReadableHeaderLabel(text: string): boolean {
	const t = text.trim();
	if (!t) {
		return false;
	}
	// Cube / connector paths are never display labels — even with spaces
	// (`googleBusinessProfile.Website Clicks`).
	if (t.includes('.')) {
		return false;
	}
	if (/\s/.test(t)) {
		return true;
	}
	if (/[a-z][A-Z]/.test(t)) {
		return false;
	}
	if (canonicalDataSourceMetaKey(t)) {
		return false;
	}
	return true;
}
